import {
  Box,
  Typography,
  Button,
  TextField,
  Tooltip,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import React, {
  FC,
  ReactElement,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { MeetingItemRepresentation } from "../models/meetingItemRepresentation";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ItemStatus from "./itemStatus";
import dateFormatter from "../util/dateFormatter";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";
import {
  MeetingItemStatus,
  MeetingItemType,
  enumFromStringValue,
} from "../models";
import { FreeSingleSelect } from "./freeSingleSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";
import { AttachmentsModal } from "./attachmentsModal";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ConfirmationPanel from "./confirmationPanel";
import { deleteQuickTask } from "../actions/quickTaskActions";
import { PaperComponent } from "../util/paperComponent";
import PersonalNotesModal from "./meetingNotes/PersonalNotesModal";
import { PersonalNote, PersonalNoteTargetType } from "../models/personalNote";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const typeOptions = [
  {
    value: MeetingItemType.Task,
    labelKey: "meetingNotes.common.itemTypes.task",
    icon: <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: MeetingItemType.Decision,
    labelKey: "meetingNotes.common.itemTypes.decision",
    icon: <GavelOutlinedIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: MeetingItemType.Info,
    labelKey: "meetingNotes.common.itemTypes.info",
    icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,
  },
];

export interface QuickTaskEditingPanelProps {
  open: boolean;
  onClose: () => void;
  allowMetadataEditing: boolean;
  allowAttachmentsEditing?: boolean;
  showPersonalNotesButton?: boolean;
  meetingItem?: MeetingItemRepresentation;
  items?: MeetingItemRepresentation[];
  onNavigateTo?: (item: MeetingItemRepresentation) => void;
  onUpdate?: (
    item: MeetingItemRepresentation,
  ) => Promise<unknown> | unknown;
  onDelete?: (item: MeetingItemRepresentation) => void;
  onAttachmentsCountChanged?: (
    count: number,
    meetingId?: string,
    itemId?: string,
  ) => Promise<void> | void;
}

export const QuickTaskEditingPanel: FC<QuickTaskEditingPanelProps> = (
  props: QuickTaskEditingPanelProps,
): ReactElement => {
  const { t } = useTranslation();
  const showPersonalNotesButton = props.showPersonalNotesButton ?? true;
  const getInitialItemType = (item: MeetingItemRepresentation): string => {
    if (item.decision) return MeetingItemType.Decision;
    if (item.info) return MeetingItemType.Info;
    return MeetingItemType.Task;
  };

  const [content, setContent] = useState(props.meetingItem.content);
  const [category, setCategory] = useState(props.meetingItem.category);
  const [status, setStatus] = useState(props.meetingItem.status);
  const [actor, setActor] = useState(props.meetingItem.actor);
  const [dueDate, setDueDate] = useState(props.meetingItem.dueDate);
  const [itemType, setItemType] = useState<string>(
    getInitialItemType(props.meetingItem),
  );
  const [warningDialogOpen, setWarningDialogOpen] = React.useState(false);
  const [pendingNavigationItem, setPendingNavigationItem] = React.useState<MeetingItemRepresentation | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsCount, setAttachmentsCount] = useState(props.meetingItem.attachments || 0);
  const [showPersonalNotesModal, setShowPersonalNotesModal] = useState(false);
  const [, setPersonalNotes] = useState<PersonalNote[]>([]);
  const appContext = useContext<AppContext>(Context);

  const isTask = itemType === MeetingItemType.Task;
  // Only disable responsible, due date, and status fields in meeting-notes context (when items list is provided)
  const isResponsibleDisabled = Boolean(props.items) && !isTask;
  const isDueDateDisabled = Boolean(props.items) && !isTask;
  const isStatusDisabled = Boolean(props.items) && !isTask;

  // Navigation
  const currentIndex = useMemo(() => {
    if (!props.items) return -1;
    return props.items.findIndex((item) => item.id === props.meetingItem?.id);
  }, [props.items, props.meetingItem?.id]);

  const hasPrevious = currentIndex > 0;
  const hasNext =
    props.items && currentIndex >= 0 && currentIndex < props.items.length - 1;

  const hasChanges = React.useMemo(() => {
    const originalType = getInitialItemType(props.meetingItem);
    return (
      props.meetingItem.content !== content ||
      props.meetingItem.category !== category ||
      props.meetingItem.actor !== actor ||
      props.meetingItem.dueDate !== dueDate ||
      props.meetingItem.status !== status ||
      originalType !== itemType
    );
  }, [content, category, actor, dueDate, status, itemType, props.meetingItem]);

  const handleNavigate = React.useCallback(
    (direction: "prev" | "next") => {
      if (!props.items || !props.onNavigateTo) return;
      const targetIndex =
        direction === "prev" ? currentIndex - 1 : currentIndex + 1;
      const targetItem = props.items[targetIndex];
      if (targetItem) {
        if (hasChanges) {
          setPendingNavigationItem(targetItem);
          setWarningDialogOpen(true);
        } else {
          props.onNavigateTo(targetItem);
        }
      }
    },
    [props.items, props.onNavigateTo, currentIndex, hasChanges],
  );

  // Sync state when dialog opens or meetingItem changes
  useEffect(() => {
    if (props.open) {
      setContent(props.meetingItem.content);
      setCategory(props.meetingItem.category);
      setStatus(props.meetingItem.status);
      setActor(props.meetingItem.actor);
      setDueDate(props.meetingItem.dueDate);
      setItemType(getInitialItemType(props.meetingItem));
      setWarningDialogOpen(false);
      setDeleteDialogOpen(false);
    }
  }, [props.open, props.meetingItem]);

  // Memoize permission checks
  const isCreator = React.useMemo(
    () => props.meetingItem.creator === appContext.state.invoker.email,
    [props.meetingItem.creator, appContext.state.invoker.email],
  );

  const editContent = React.useMemo(
    () => props.allowMetadataEditing && isCreator,
    [props.allowMetadataEditing, isCreator],
  );

  const categoryReadOnly = React.useMemo(
    () => Boolean(props.meetingItem?.isCoachingTask),
    [props.meetingItem?.isCoachingTask],
  );

  const editActor = React.useMemo(
    () => props.allowMetadataEditing && isCreator,
    [props.allowMetadataEditing, isCreator],
  );

  const editDueDate = React.useMemo(() => true, []);

  const editStatus = React.useMemo(() => true, []);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      props.onClose();
    }
  }, [hasChanges, props.onClose]);

  const buildUpdatedRep = React.useCallback(
    (): MeetingItemRepresentation =>
      (({
        ...props.meetingItem,
        content,
        category,
        actor: isResponsibleDisabled ? undefined : actor,
        status,
        dueDate: isDueDateDisabled ? undefined : dueDate,

        ...(props.items
          ? {
              task: itemType === MeetingItemType.Task,
              decision: itemType === MeetingItemType.Decision,
              info: itemType === MeetingItemType.Info,
            }
          : {})
      }) as MeetingItemRepresentation),
    [
      props.meetingItem,
      content,
      category,
      actor,
      isResponsibleDisabled,
      isDueDateDisabled,
      itemType,
      status,
      dueDate,
      props.items,
    ],
  );

  const saveChanges = React.useCallback(async () => {
    // Let parent handle the actual save to avoid duplicate PUTs
    try {
      await props.onUpdate?.(buildUpdatedRep());
      setSuccessSnackbarOpen(true);
      props.onClose();
    } catch (err) {
      // Error Handling needed
    }
  }, [buildUpdatedRep, props.onUpdate, props.onClose]);

  const saveAndNavigate = React.useCallback(async () => {
    if (!pendingNavigationItem || !props.onNavigateTo) return;
    try {
      await props.onUpdate?.(buildUpdatedRep());
      setSuccessSnackbarOpen(true);
      setWarningDialogOpen(false);
      props.onNavigateTo(pendingNavigationItem);
      setPendingNavigationItem(null);
    } catch (err) {
      // Error Handling needed
    }
  }, [buildUpdatedRep, props.onUpdate, props.onNavigateTo, pendingNavigationItem]);

  const handleDelete = React.useCallback(async () => {
    if (!props.meetingItem.meetingId) {
      await deleteQuickTask(props.meetingItem.id);
      if (props.onDelete) {
        props.onDelete(props.meetingItem);
      }
      props.onClose();
    }
  }, [props.meetingItem, props.onDelete, props.onClose]);

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="md"
        onClose={handleWarningClose}
        aria-labelledby="draggable-dialog-title"
        open={props.open}
        keepMounted={false}
        PaperComponent={PaperComponent} slotProps={{ paper: {
          style: {
            borderRadius: "8px",
            backgroundColor: "white",
          },
        } }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <DialogTitle
            sx={{ textAlign: "left", ...({ display: "flex", alignItems: "center", gap: 1 }) }}
          >
            {props.items
              ? `${t("quicktask.edit_item_title")} #${String(currentIndex + 1).padStart(3, "0")}`
              : props.meetingItem?.identifier
                ? `${t("quicktask.edit_own_task.title")} ${props.meetingItem.identifier}`
                : t("quicktask.edit_own_task.title")}
            {props.items && props.items.length > 1 && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                <IconButton
                  size="small"
                  disabled={!hasPrevious}
                  onClick={() => handleNavigate("prev")}
                  aria-label="previous item"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!hasNext}
                  onClick={() => handleNavigate("next")}
                  aria-label="next item"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleWarningClose}
            sx={{
              position: "absolute",
              right: 12,
              color: "#5F6368",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* item type — only shown when navigating meeting items */}
            {props.items && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle2" component="label">
                  {t("quicktask.type_title")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {typeOptions.map((option) => {
                    const isSelected = itemType === option.value;
                    return (
                      <Button
                        key={option.value}
                        size="small"
                        onClick={() => {
                          setItemType(option.value);
                          if (option.value !== MeetingItemType.Task) {
                            setActor(undefined);
                            setDueDate(null);
                          }
                        }}
                        startIcon={option.icon}
                        sx={[{
                          textTransform: "none",
                          borderRadius: "999px",
                          px: 1.5,
                          minWidth: 0,
                          border: "1px solid",

                          "&:hover": {
                            borderColor: "#242C63"
                          }
                        }, isSelected ? {
                          color: "#FFFFFF"
                        } : {
                          color: "#5C6B85"
                        }, isSelected ? {
                          backgroundColor: "#242C63"
                        } : {
                          backgroundColor: "#FFFFFF"
                        }, isSelected ? {
                          borderColor: "#242C63"
                        } : {
                          borderColor: "#d2d2d2"
                        }, isSelected ? {
                          "&:hover": {
                            backgroundColor: "#1a2048"
                          }
                        } : {
                          "&:hover": {
                            backgroundColor: "#F8FAFC"
                          }
                        }]}
                      >
                        {t(option.labelKey)}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* task category */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="category-input"
              >
                {t("quicktask.category_title")}
              </Typography>
              <FreeSingleSelect
                options={[]}
                value={category || ""}
                placeholder={t("quicktask.category_placeholder")}
                onValueSelected={(value) => setCategory(value)}
                useCategorySuggestions={true}
                enableUsageSort={true}
                enableDelete={true}
                maxVisibleItems={6}
                hideTooltip={true}
                disabled={categoryReadOnly}
              />
            </Box>

            {/* task description */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="description-input"
              >
                {t("quicktask.description_title")} *
              </Typography>
              {editContent ? (
                <TextField
                  id="description-input"
                  required
                  placeholder={t("quicktask.description_placeholder")}
                  multiline
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "black",
                      opacity: 1,
                    },
                  }}
                />
              ) : (
                <Typography variant="body2">{content}</Typography>
              )}
            </Box>

            {/* task responsible, due date, status in one row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* responsible */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" component="label">
                  {t("quicktask.responsible_title")} {!isResponsibleDisabled && "*"}
                </Typography>
                <Tooltip
                  title={
                    isResponsibleDisabled
                      ? t(
                          "meetingNotes.takeNotes.tooltips.responsibleDisabled",
                        )
                      : ""
                  }
                  placement="top"
                  arrow
                >
                  <Box>
                    {editActor ? (
                      <FreeSingleSelect
                        onValueSelected={(value) => setActor(value)}
                        options={[
                          ...(appContext.state.selectedList?.participants ||
                            []),
                          ...(appContext.state.invoker?.invitedUserEmails ||
                            []),
                        ]}
                        value={isResponsibleDisabled ? "" : actor}
                        placeholder={t("quicktask.responsible_placeholder")}
                        hideTooltip={true}
                        disabled={isResponsibleDisabled}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={[{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "200px",
                          fontSize: "16px",
                          mt: 1
                        }, isResponsibleDisabled ? {
                          color: "grey.400"
                        } : {
                          color: "black"
                        }]}
                      >
                        {!isResponsibleDisabled
                          ? props.meetingItem.actor || t("item.notAvailable")
                          : t("item.notAvailable")}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              </Box>

              {/* due date */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" component="label">
                  {t("quicktask.dueDate_title")}
                </Typography>
                {editDueDate ? (
                  <Tooltip
                    title={
                      isDueDateDisabled
                        ? t(
                            "meetingNotes.takeNotes.tooltips.dueDateDisabled",
                          )
                        : ""
                    }
                    placement="top"
                    arrow
                  >
                    <Box sx={{ width: "100%" }}>
                      <DatePicker
                        className="lighter-textfield"
                        disabled={isDueDateDisabled}
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-input": {
                            color: "black",
                          },
                          ...(isDueDateDisabled && {
                            "& .MuiInputBase-root.Mui-disabled, & .MuiInputBase-root.Mui-disabled:hover, & .MuiPickersOutlinedInput-root.Mui-disabled, & .MuiPickersOutlinedInput-root.Mui-disabled:hover, & .MuiOutlinedInput-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled:hover":
                              {
                                backgroundColor: "#f5f5f5",
                              },
                            "& .MuiInputBase-input.Mui-disabled, & .MuiPickersOutlinedInput-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled":
                              {
                                WebkitTextFillColor: "#B0B0B0",
                                color: "#B0B0B0",
                              },
                            "& .Mui-disabled .MuiSvgIcon-root, & .MuiSvgIcon-root.Mui-disabled":
                              {
                                color: "#B0B0B0",
                              },
                          }),
                        }}
                        value={
                          isDueDateDisabled
                            ? null
                            : dueDate
                              ? dayjs(dueDate)
                              : null
                        }
                        onChange={(newDate) =>
                          setDueDate(newDate ? newDate.toDate() : null)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            "aria-label": t("quicktask.dueDate_title"),
                          },
                        }}
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <Typography variant="body2">
                    {props.meetingItem.dueDate
                      ? dateFormatter.format(
                          new Date(props.meetingItem.dueDate),
                        )
                      : t("item.notAvailable")}
                  </Typography>
                )}
              </Box>

              {/* status */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="label"
                  htmlFor="status-select"
                >
                  {t("quicktask.status_title")}
                </Typography>
                {editStatus ? (
                  <FormControl fullWidth size="small" disabled={isStatusDisabled}>
                    <Select
                      id="status-select"
                      value={status}
                      disabled={isStatusDisabled}
                      onChange={(e) =>
                        setStatus(
                          enumFromStringValue(
                            MeetingItemStatus,
                            e.target.value,
                          ),
                        )
                      }
                      sx={{
                        "& .MuiSelect-select": {
                          fontFamily: "inherit",
                          fontSize: "1rem",
                          color: "black",
                        },
                        ...(isStatusDisabled && {
                          "&.Mui-disabled": {
                            backgroundColor: "#f5f5f5",
                          },
                          "&.Mui-disabled .MuiSelect-select": {
                            WebkitTextFillColor: "#B0B0B0",
                            color: "#B0B0B0",
                          },
                          "&.Mui-disabled .MuiSvgIcon-root": {
                            color: "#B0B0B0",
                          },
                        }),
                      }}
                      MenuProps={{ slotProps: { paper: {
                          sx: {
                            backgroundColor: "white",
                            "& .MuiMenuItem-root": {
                              fontSize: "1rem",
                              fontFamily: "inherit",
                            },
                          },
                        } } }}
                    >
                      <MenuItem value={MeetingItemStatus.NotStarted}>
                        {t(`item_status.${MeetingItemStatus.NotStarted}`)}
                      </MenuItem>
                      <MenuItem value={MeetingItemStatus.InProgress}>
                        {t(`item_status.${MeetingItemStatus.InProgress}`)}
                      </MenuItem>
                      <MenuItem value={MeetingItemStatus.OnHold}>
                        {t(`item_status.${MeetingItemStatus.OnHold}`)}
                      </MenuItem>
                      <MenuItem value={MeetingItemStatus.Completed}>
                        {t(`item_status.${MeetingItemStatus.Completed}`)}
                      </MenuItem>
                      <MenuItem value={MeetingItemStatus.Cancelled}>
                        {t(`item_status.${MeetingItemStatus.Cancelled}`)}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Box sx={{ width: "150px" }}>
                    <ItemStatus
                      status={props.meetingItem.status}
                      lastModifiedBy={props.meetingItem.actor}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            {t("button.delete")}
          </Button>
          {showPersonalNotesButton ? (
            <Button
              variant="outlined"
              startIcon={<LockOutlinedIcon />}
              onClick={() => setShowPersonalNotesModal(true)}
            >
              {t("meetingNotes.personalNotes.sectionTitle")}
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => setShowAttachments(true)}
          >
            {t("dashboard.itemAttachments_label", { count: attachmentsCount })}
          </Button>
          <Button
            variant="contained"
            autoFocus
            disabled={!hasChanges || !content.trim()}
            onClick={saveChanges}
          >
            {t("item.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Attachments Modal */}
      <AttachmentsModal
        open={showAttachments}
        allowModifications={props.allowAttachmentsEditing}
        height="70vh"
        uploadPrefix={
          props.meetingItem.meetingId
            ? `items/meetings/${props.meetingItem.meetingId}/items/${props.meetingItem.id}/`
            : `items/quicktasks/${props.meetingItem.id}/`
        }
        meetingId={props.meetingItem.meetingId}
        itemId={props.meetingItem.id}
        onClose={() => setShowAttachments(false)}
        onAttachmentsCountChanged={(
          count: number,
          meetingId?: string,
          itemId?: string,
        ) => {
          props.meetingItem.attachments = count;
          setAttachmentsCount(count);
          props.onAttachmentsCountChanged?.(count, meetingId, itemId);
        }}
      />
      {/* Personal Notes Modal */}
      {showPersonalNotesButton ? (
        <PersonalNotesModal
          open={showPersonalNotesModal}
          onClose={() => setShowPersonalNotesModal(false)}
          targetType={
            props.meetingItem?.isCoachingTask
              ? PersonalNoteTargetType.CoachingTask
              : PersonalNoteTargetType.QuickTask
          }
          targetId={props.meetingItem?.id || null}
          targetItem={props.meetingItem}
          onNotesChange={(notes) => setPersonalNotes(notes)}
        />
      ) : null}
      {/* Delete Confirmation Dialog */}
      <ConfirmationPanel
        showModal={deleteDialogOpen}
        hideModal={() => setDeleteDialogOpen(false)}
        confirmModal={handleDelete}
        action="delete"
      />
      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        content={
          pendingNavigationItem ? t("text.warning.continue_message") : undefined
        }
        hideModal={() => {
          setWarningDialogOpen(false);
          setPendingNavigationItem(null);
        }}
        confirmModal={() => {
          setWarningDialogOpen(false);
          if (pendingNavigationItem) {
            props.onNavigateTo?.(pendingNavigationItem);
            setPendingNavigationItem(null);
          } else {
            props.onClose();
          }
        }}
        customActions={
          pendingNavigationItem ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setWarningDialogOpen(false);
                  props.onNavigateTo?.(pendingNavigationItem);
                  setPendingNavigationItem(null);
                }}
              >
                {t("text.warning.continue_without_saving")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!content.trim()}
                onClick={saveAndNavigate}
                autoFocus
              >
                {t("text.warning.save_and_continue")}
              </Button>
            </>
          ) : undefined
        }
      />
      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          icon={<CheckIcon fontSize="inherit" />}
        >
          {t("quicktask.edit_own_task.success_message")}
        </Alert>
      </Snackbar>
    </>
  );
};
